// ManagerHome.jsx
import React, { useEffect, useState } from "react";
import "./ManagerHome.css";

const API_BASE = process.env.REACT_APP_API_BASE || `${process.env.REACT_APP_API_URL}`;

function apiHeaders(json = true) {
  const headers = {};
  if (json) headers["Content-Type"] = "application/json";
  const token = localStorage.getItem("token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

export default function ManagerHome() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function loadAll() {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token provided. Please log in.");

        const [projRes, clientsRes, empRes] = await Promise.all([
          fetch(`${API_BASE}/api/projects`, { headers: apiHeaders(false) }),
          fetch(`${API_BASE}/api/clients`, { headers: apiHeaders(false) }),
          fetch(`${API_BASE}/api/employees`, { headers: apiHeaders(false) }),
        ]);

        const projBody = projRes.ok ? await projRes.json() : [];
        const clientsBody = clientsRes.ok ? await clientsRes.json() : [];
        const empBody = empRes.ok ? await empRes.json() : [];

        if (!projRes.ok)
          throw new Error(`Failed to load projects (${projRes.status})`);
        if (!clientsRes.ok)
          throw new Error(`Failed to load clients (${clientsRes.status})`);
        if (!empRes.ok)
          throw new Error(`Failed to load employees (${empRes.status})`);

        if (mounted) {
          setProjects(Array.isArray(projBody) ? projBody : []);
          setClients(Array.isArray(clientsBody) ? clientsBody : []);
          setEmployees(Array.isArray(empBody) ? empBody : []);
        }
      } catch (err) {
        console.error("ManagerHome load error:", err);
        if (mounted) setError(err.message || "Failed to load data");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadAll();
    return () => {
      mounted = false;
    };
  }, []);

  // Compute metrics
  const totalProjects = projects.length;
  const totalClients = clients.length;
  const totalEmployees = employees.length;

  const endedStatuses = new Set(["completed", "done", "cancelled", "closed"]);
  const ongoingProjects = projects.filter((p) => {
    const s = (p.status || "").toLowerCase();
    return !endedStatuses.has(s);
  }).length;

  return (
    <div className="manager-home-container">
      <div className="manager-home-header">
        <h2>Manager Dashboard</h2>
        <p className="sub">
          Overview of projects, clients, and team performance
        </p>
      </div>

      {loading ? (
        <div className="manager-loading">Loading…</div>
      ) : error ? (
        <div className="manager-error">Error: {error}</div>
      ) : (
        <div className="manager-cards-wrapper">
          <div className="manager-cards-grid">
            <div className="manager-card accent">
              <div className="card-body">
                <div className="card-title">Total Projects</div>
                <div className="card-value">{totalProjects}</div>
                <div className="card-note">All projects under management</div>
              </div>
            </div>

            <div className="manager-card">
              <div className="card-body">
                <div className="card-title">Ongoing Projects</div>
                <div className="card-value">{ongoingProjects}</div>
                <div className="card-note">Currently active projects</div>
              </div>
            </div>

            <div className="manager-card">
              <div className="card-body">
                <div className="card-title">Total Clients</div>
                <div className="card-value">{totalClients}</div>
                <div className="card-note">Registered project clients</div>
              </div>
            </div>

            <div className="manager-card">
              <div className="card-body">
                <div className="card-title">Total Employees</div>
                <div className="card-value">{totalEmployees}</div>
                <div className="card-note">Team members in the system</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* <div className="manager-footer-note">
        <small>Data auto-fetched from your existing APIs.</small>
      </div> */}
    </div>
  );
}
